import { Box, Button, Tooltip } from '@mui/material';
import { CommentNoteIcon } from '@synergy/theme-next';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloConfirmationDialog,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
    PaloThumb,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { SponsorBanner, SponsorBannerStatus, getSponsorBannerTranslationId } from '@synergy/models';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as SponsorBannerActions from '../store/sponsor-banner-actions';
import * as SponsorBannerListActions from '../sponsor-banner-list/store/sponsor-banner-list-actions';
import * as SponsorBannerListSelectors from '../sponsor-banner-list/store/sponsor-banner-list-selectors';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/** Sponsor banners have no stored status; it is derived from the validity window. */
const statusOf = (banner: SponsorBanner): SponsorBannerStatus => {
    const now = new Date();
    const validFrom = new Date(banner.validFrom);
    validFrom.setHours(0, 0, 0, 0);
    const validTo = new Date(banner.validTo);
    validTo.setHours(23, 59, 59, 999);

    return now >= validFrom && now <= validTo ? SponsorBannerStatus.Active : SponsorBannerStatus.Inactive;
};

const columns: PaloColumn<SponsorBanner>[] = [
    {
        key: 'status',
        type: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: banner => {
            const status = statusOf(banner);

            return (
                <PaloStatusPill
                    status={status}
                    label={<FormattedMessage id={getSponsorBannerTranslationId(status)} />}
                />
            );
        },
    },
    {
        key: 'picture',
        type: 'icon',
        head: <FormattedMessage id="app.table.column.picture" />,
        cell: banner => <PaloThumb src={banner.previewImage} width={56} height={26} sx={{ objectFit: 'contain' }} />,
    },
    {
        key: 'supplier',
        link: true,
        type: 'name',
        head: <FormattedMessage id="app.table.column.supplier" />,
        cell: banner => banner.supplier?.name,
    },
    {
        key: 'hint',
        type: 'flag',
        head: <FormattedMessage id="app.contentManagement.sponsorBanner.hint" />,
        cell: banner =>
            banner.note ? (
                <Tooltip title={banner.note}>
                    <Box sx={{ display: 'inline-flex' }}>
                        <CommentNoteIcon sx={{ fontSize: 18 }} />
                    </Box>
                </Tooltip>
            ) : null,
    },
    {
        key: 'validFrom',
        type: 'date',
        sortKey: 'validFrom',
        head: <FormattedMessage id="app.table.column.validFrom" />,
        cell: banner => <FormattedDate value={banner.validFrom} year="numeric" month="short" day="2-digit" />,
    },
    {
        key: 'validTo',
        type: 'date',
        sortKey: 'validTo',
        head: <FormattedMessage id="app.table.column.validTo" />,
        cell: banner => <FormattedDate value={banner.validTo} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloSponsorBannerList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('sponsor-banners');
    const columnFilter = usePaloColumnFilters('sponsor-banners');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('validFrom', 'desc');
    const requesting = useAppSelector(SponsorBannerListSelectors.sponsorBannerListRequestingSelector);
    const paginated = useAppSelector(SponsorBannerListSelectors.sponsorBannerListItemsSelector);
    const [toDelete, setToDelete] = useState<number | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(SponsorBannerListActions.fetchSponsorBannerList(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (banner: SponsorBanner): string =>
        ROUTES.contentManagement.sponsorBanner.detail.replace(':id', banner.id.toString());

    const openDetail = (banner: SponsorBanner): void => navigate(detailHref(banner));

    const handleDelete = (): void => {
        if (toDelete) {
            dispatch(SponsorBannerActions.deleteSponsorBanner(toDelete));
            setToDelete(null);
        }
    };

    const rowActions = (banner: SponsorBanner): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(banner) },
        { label: 'Löschen', onClick: () => setToDelete(banner.id) },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${ROUTES.contentManagement.sponsorBanner.list}/new`)}
        >
            <FormattedMessage id="app.contentManagement.newSponsorBanner" />
        </Button>
    );

    return (
        <PaloListView<SponsorBanner>
            pageTitle={intl.formatMessage({ id: 'app.contentManagement.sponsorBanner' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            title={<FormattedMessage id="app.contentManagement.sponsorBanner" />}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="sponsor-banners" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={banner => banner.id}
            requesting={requesting}
            emptyLabel="Keine Sponsor-Banner."
            rowHref={detailHref}
            rowActions={rowActions}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <PaloConfirmationDialog
                isVisible={!!toDelete}
                onConfirm={handleDelete}
                onCancel={() => setToDelete(null)}
                title={<FormattedMessage id="app.contentManagement.sponsorBanner.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <FormattedMessage id="app.contentManagement.sponsorBanner.deleteWarning" />
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
